import { EditIcon, TrashIcon } from "lucide-react";
import { useState, useMemo } from "react";

type EmailModel = {
  _id: string;
  name?: string;
  description?: string;
  html?: string;
  text?: string;
  type?: string;
  replyRate?: number;
  lang?: "en" | "fr";
};

const defaultFreelanceBodyEN = `Hi {firstName},

I'm a freelance {title} who helps teams like {company} ship faster with less risk.

I had a quick idea for {company} you could test in ~20 minutes:

• Replace the most time-consuming step with a lightweight checklist/automation
• Measure the impact on response rate or time-to-close this week

If this is useful, I can record a 3‑min Loom tailored to {company} showing exactly how I'd do it.

Open to a quick 10‑min chat next week?

Best,`;

const defaultFreelanceBodyFR = `Bonjour {firstName},

Je suis freelance {title} et j'aide des équipes comme {company} à livrer plus vite avec moins de risques.

J'ai une idée rapide pour {company} à tester en ~20 minutes :

• Remplacer l'étape la plus chronophage par une checklist/automation légère
• Mesurer l'impact sur le taux de réponse ou le time‑to‑close cette semaine

Si utile, je peux enregistrer un Loom de 3 min, adapté à {company}, pour montrer exactement comment je ferais.

Ouvert à un échange de 10 min la semaine prochaine ?

Bien à vous,`;

interface TemplatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: EmailModel) => void;
  templates: EmailModel[];
  language: "en" | "fr";
  onLanguageChange: (lang: "en" | "fr") => void;
  onCreateTemplate: (template: { name: string; description?: string; text: string; type: string }) => Promise<void>;
  onUpdateTemplate: (id: string, updates: any) => Promise<void>;
  onDeleteTemplate: (id: string) => Promise<void>;
  onSetLastUsedTemplate?: (id: string) => Promise<void>;
  applyTemplate: (text: string) => string;
}

const Token = ({ label, onInsert }: { label: string; onInsert: (t: string) => void }) => (
  <button
    type="button"
    className="text-xs text-gray-700 hover:text-gray-800 cursor-pointer"
    onClick={() => onInsert(label)}
    title={`Insert ${label}`}
  >
    {label.replace("{", "").replace("}", "")}
  </button>
);

export default function TemplatesModal({
  isOpen,
  onClose,
  onSelectTemplate,
  templates,
  language,
  onLanguageChange,
  onCreateTemplate,
  onUpdateTemplate,
  onDeleteTemplate,
  onSetLastUsedTemplate,
  applyTemplate
}: TemplatesModalProps) {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | "email" | "linkedin">("all");
  const [currentView, setCurrentView] = useState<"list" | "create" | "edit">("list");
  const [newTemplate, setNewTemplate] = useState<{ name: string; description?: string; text: string; type: string }>({ 
    name: "",
    description: "",
    text: "",
    type: "email"
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<{ name?: string; description?: string; text?: string; type?: string }>({});
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const filteredTemplates = useMemo(() => {
    // Put user templates first, then local presets
    const list: EmailModel[] = [...templates];
    const q = search.trim().toLowerCase();

    // Filter by type and exclude SMS
    let filtered = list
      .filter((t) => (t.type ? t.type !== "sms" : true))
      .filter((t) => !!(t.html || t.text));

    // Apply type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter((t) => {
        const templateType = t.type || "email"; // Default to email if no type
        return templateType === typeFilter;
      });
    }

    // Apply search filter
    if (!q) return filtered;
    return filtered.filter((t) =>
      [t.name, t.description].filter(Boolean).some((v: any) => String(v).toLowerCase().includes(q))
    );
  }, [templates, search, typeFilter, language]);

  const handleSelectTemplate = async (template: EmailModel) => {
    onSelectTemplate(template);
    try {
      if (!template._id.includes("idea-") && onSetLastUsedTemplate) {
        await onSetLastUsedTemplate(template._id);
      }
    } catch {}
    onClose();
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.name?.trim() || !newTemplate.text?.trim()) return;
    try {
      await onCreateTemplate({
        name: newTemplate.name.trim(),
        description: (newTemplate.description || "").trim() || undefined,
        text: newTemplate.text,
        type: newTemplate.type,
      });
      setNewTemplate({ name: "", description: "", text: "", type: "email" });
      setCurrentView("list");
    } catch (err) {
      console.warn("Failed to create template", err);
    }
  };

  const startEditTemplate = (t: EmailModel) => {
    setEditingId(t._id);
    setEditDraft({ 
      name: t.name || "", 
      description: t.description || "", 
      text: t.text || t.html || "", 
      type: t.type || "email" 
    });
    setCurrentView("edit");
  };

  const cancelEditTemplate = () => {
    setEditingId(null);
    setEditDraft({});
    setCurrentView("list");
  };

  const handleUpdateTemplate = async () => {
    if (!editingId) return;
    const updates: any = {
      ...(editDraft.name !== undefined ? { name: editDraft.name } : {}),
      ...(editDraft.description !== undefined ? { description: editDraft.description } : {}),
      ...(editDraft.text !== undefined ? { text: editDraft.text } : {}),
      ...(editDraft.type !== undefined ? { type: editDraft.type } : {}),
    };
    try {
      await onUpdateTemplate(editingId, updates);
      cancelEditTemplate();
    } catch (err) {
      console.warn("Failed to update template", err);
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    try {
      await onDeleteTemplate(id);
      if (editingId === id) cancelEditTemplate();
      if (confirmDeleteId === id) setConfirmDeleteId(null);
    } catch (err) {
      console.warn("Failed to delete template", err);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Templates Modal - Marketplace Style */}
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/40" onClick={() => {
          setCurrentView("list");
          onClose();
        }} />
        <div className="relative bg-white w-full max-w-6xl mx-4 h-[90vh] rounded shadow-xl border border-gray-200 flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
            <div>
              <div className="text-lg font-semibold text-gray-900">
                {currentView === "create" ? "Create New Template" : 
                 currentView === "edit" ? "Edit Template" : "Templates"}
              </div>
              <div className="text-xs text-gray-600 mt-1">
                {currentView === "create" 
                  ? "Design your email template" 
                  : currentView === "edit"
                  ? "Update your email template"
                  : "Choose from our marketplace of proven templates"
                }
              </div>
            </div>
            <button
              className="px-4 py-2 text-xs border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50"
              onClick={() => {
                setCurrentView("list");
                onClose();
              }}
            >
              Close
            </button>
          </div>

          {/* Controls */}
          {currentView === "list" && (
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-4 flex-shrink-0">
              <div className="flex-1 max-w-md">
                <input
                  className="w-full px-4 py-2 text-xs border border-gray-300 rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="Search templates..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  className="px-4 py-2 text-xs bg-black text-white rounded-sm hover:bg-black/80 flex items-center gap-2"
                  onClick={() => setCurrentView("create")}
                >
                  <span>+</span> New Template
                </button>
                <div className="flex border border-gray-300 rounded-sm overflow-hidden">
                  <button
                    className={`px-3 py-2 text-xs font-medium transition ${
                      typeFilter === "all"
                        ? "bg-black text-white"
                        : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                    onClick={() => setTypeFilter("all")}
                  >
                    All
                  </button>
                  <button
                    className={`px-2 py-2 text-xs font-medium transition ${
                      typeFilter === "email"
                        ? "bg-black text-white"
                        : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                    onClick={() => setTypeFilter("email")}
                  >
                    Email
                  </button>
                  <button
                    className={`px-2 py-2 text-xs font-medium transition ${
                      typeFilter === "linkedin"
                        ? "bg-black text-white"
                        : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                    onClick={() => setTypeFilter("linkedin")}
                  >
                    LinkedIn
                  </button>
                </div>
                <div className="flex border border-gray-300 rounded-sm overflow-hidden">
                  <button
                    className={`px-2 py-2 text-xs font-medium transition ${
                      language === "en"
                        ? "bg-black text-white"
                        : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                    onClick={() => onLanguageChange("en")}
                  >
                    EN
                  </button>
                  <button
                    className={`px-4 py-2 text-xs font-medium transition ${
                      language === "fr"
                        ? "bg-black text-white"
                        : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                    onClick={() => onLanguageChange("fr")}
                  >
                    FR
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Content Area */}
          <div className="flex-1 overflow-auto p-6">
            {currentView === "list" ? (
              // Template List View
              !filteredTemplates.length ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="text-gray-400 text-lg mb-2">📧</div>
                  <div className="text-lg font-medium text-gray-900 mb-1">No templates found</div>
                  <div className="text-xs text-gray-600">Try adjusting your search or create a new template</div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredTemplates.map((t) => {
                    const isLocal = (t._id || "").startsWith("idea-");
                    const previewText = applyTemplate(t.text || t.html || "");
                    const truncatedPreview = previewText.length > 150 ? previewText.substring(0, 150) + "..." : previewText;

                    return (
                      <div key={t._id} className="group relative">
                        <div
                          className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-900 hover:shadow-lg transition-all duration-200 cursor-pointer h-full flex flex-col"
                          onClick={() => handleSelectTemplate(t)}
                        >
                          {/* Template Header */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold text-gray-900 mb-1 line-clamp-1">
                                {t.name || "Untitled"}
                              </div>
                              {t.description && (
                                <div className="text-xs text-gray-600 line-clamp-2">
                                  {t.description}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Template Preview */}
                          <div className="flex-1 bg-gray-50 rounded-sm p-3 mb-3">
                            <div className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
                              {truncatedPreview}
                            </div>
                          </div>

                          {/* Template Footer */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {isLocal ? (
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                                  Built-in
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded">
                                  Custom
                                </span>
                              )}
                              <span className={`px-2 py-1 text-xs font-medium rounded ${
                                (t.type || "email") === "email"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}>
                                {(t.type || "email") === "email" ? "Email" : "LinkedIn"}
                              </span>
                              <span className="text-xs text-gray-500 uppercase">
                                {t.lang || 'en'}
                              </span>
                            </div>
                            {!isLocal && (
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                <button
                                  className="p-1 text-gray-400 hover:text-gray-600 text-xs"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startEditTemplate(t);
                                  }}
                                  title="Edit template"
                                >
                                  <EditIcon className="h-4 w-4 text-black" />
                                </button>
                                <button
                                  className="p-1 text-gray-400 hover:text-red-600 text-xs"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setConfirmDeleteId(t._id);
                                  }}
                                  title="Delete template"
                                >
                                  <TrashIcon className="h-4 w-4 text-black" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Delete Confirmation */}
                        {confirmDeleteId === t._id && (
                          <div className="absolute inset-0 bg-red-50 border-2 border-red-200 rounded-lg p-4 flex flex-col items-center justify-center">
                            <div className="text-xs font-medium text-red-900 mb-2">Delete this template?</div>
                            <div className="flex gap-2">
                              <button
                                className="px-3 py-1 text-xs border border-gray-300 rounded bg-white text-black hover:bg-gray-50" 
                                onClick={() => setConfirmDeleteId(null)}
                              >
                                Cancel
                              </button>
                              <button 
                                className="px-3 py-1 text-xs border border-red-600 rounded text-white bg-red-600 hover:bg-red-700" 
                                onClick={() => handleDeleteTemplate(t._id)}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            ) : currentView === "create" ? (
              // Create Template View
              <div className="max-w-3xl mx-auto">
                {/* Back Button */}
                <div className="mb-6">
                  <button
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-xs"
                    onClick={() => setCurrentView("list")}
                  >
                    <span>←</span> Back to Templates
                  </button>
                </div>

                {/* Template Name */}
                <div className="pb-4">
                  <div className="flex items-center">
                    <div className="w-24 text-xs text-gray-900">Object</div>
                    <input
                      className="flex-1 bg-transparent text-xs text-gray-900 placeholder-gray-400 focus:outline-none border-none"
                      placeholder="Enter template name..."
                      value={newTemplate.name}
                      onChange={(e) => setNewTemplate((p) => ({ ...p, name: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Separator Line */}
                <div className="border-t border-gray-200"></div>

                {/* Template Description */}
                <div className="py-4">
                  <div className="flex items-center">
                    <div className="w-24 text-xs text-gray-900">Template name</div>
                    <input
                      className="flex-1 bg-transparent text-xs text-gray-900 placeholder-gray-400 focus:outline-none border-none"
                      placeholder="Brief description (optional)..."
                      value={newTemplate.description}
                      onChange={(e) => setNewTemplate((p) => ({ ...p, description: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Separator Line */}
                <div className="border-t border-gray-200"></div>

                {/* Template Type */}
                <div className="py-4">
                  <div className="flex items-center">
                    <div className="w-24 text-xs text-gray-900">Type</div>
                    <div className="flex border border-gray-300 rounded-sm overflow-hidden">
                      <button
                        type="button"
                        className={`px-2 py-1 text-xs font-medium transition ${
                          newTemplate.type === "email"
                            ? "bg-black text-white"
                            : "bg-white text-gray-700 hover:bg-gray-100"
                        }`}
                        onClick={() => setNewTemplate((p) => ({ ...p, type: "email" }))}
                      >
                        Email
                      </button>
                      <button
                        type="button"
                        className={`px-2 py-1 text-xs font-medium transition ${
                          newTemplate.type === "linkedin"
                            ? "bg-black text-white"
                            : "bg-white text-gray-700 hover:bg-gray-100"
                        }`}
                        onClick={() => setNewTemplate((p) => ({ ...p, type: "linkedin" }))}
                      >
                        LinkedIn
                      </button>
                    </div>
                  </div>
                </div>

                {/* Separator Line */}
                <div className="border-t border-gray-200"></div>

                {/* Variables Section */}
                <div className="py-3">
                  <div className="flex items-center">
                    <div className="text-xs text-gray-800">Variables</div>
                    <div className="ml-6 flex gap-6">
                      <Token label="{firstName}" onInsert={(token) => {
                        const textarea = document.querySelector('textarea[placeholder="Write your email template..."]') as HTMLTextAreaElement;
                        if (textarea) {
                          const start = textarea.selectionStart || 0;
                          const end = textarea.selectionEnd || 0;
                          const value = textarea.value || "";
                          const newValue = value.slice(0, start) + token + value.slice(end);
                          setNewTemplate((p) => ({ ...p, text: newValue }));
                          requestAnimationFrame(() => {
                            const pos = start + token.length;
                            textarea.setSelectionRange(pos, pos);
                            textarea.focus();
                          });
                        }
                      }} />
                      <Token label="{lastName}" onInsert={(token) => {
                        const textarea = document.querySelector('textarea[placeholder="Write your email template..."]') as HTMLTextAreaElement;
                        if (textarea) {
                          const start = textarea.selectionStart || 0;
                          const end = textarea.selectionEnd || 0;
                          const value = textarea.value || "";
                          const newValue = value.slice(0, start) + token + value.slice(end);
                          setNewTemplate((p) => ({ ...p, text: newValue }));
                          requestAnimationFrame(() => {
                            const pos = start + token.length;
                            textarea.setSelectionRange(pos, pos);
                            textarea.focus();
                          });
                        }
                      }} />
                      <Token label="{company}" onInsert={(token) => {
                        const textarea = document.querySelector('textarea[placeholder="Write your email template..."]') as HTMLTextAreaElement;
                        if (textarea) {
                          const start = textarea.selectionStart || 0;
                          const end = textarea.selectionEnd || 0;
                          const value = textarea.value || "";
                          const newValue = value.slice(0, start) + token + value.slice(end);
                          setNewTemplate((p) => ({ ...p, text: newValue }));
                          requestAnimationFrame(() => {
                            const pos = start + token.length;
                            textarea.setSelectionRange(pos, pos);
                            textarea.focus();
                          });
                        }
                      }} />
                      <Token label="{title}" onInsert={(token) => {
                        const textarea = document.querySelector('textarea[placeholder="Write your email template..."]') as HTMLTextAreaElement;
                        if (textarea) {
                          const start = textarea.selectionStart || 0;
                          const end = textarea.selectionEnd || 0;
                          const value = textarea.value || "";
                          const newValue = value.slice(0, start) + token + value.slice(end);
                          setNewTemplate((p) => ({ ...p, text: newValue }));
                          requestAnimationFrame(() => {
                            const pos = start + token.length;
                            textarea.setSelectionRange(pos, pos);
                            textarea.focus();
                          });
                        }
                      }} />
                    </div>
                  </div>
                </div>

                {/* Template Body */}
                <div className="">
                  <textarea
                    className="w-full h-[250px] p-3 bg-transparent border border-gray-200 text-[13px] text-black placeholder-gray-400 focus:outline-none resize-none"
                    placeholder="Write your email template..."
                    value={newTemplate.text}
                    onChange={(e) => setNewTemplate((p) => ({ ...p, text: e.target.value }))}
                  />
                </div>

                {/* Bottom Actions */}
                <div className="ml-5 flex items-center justify-between pt-4">
                  <div className="flex items-center">
                    <button
                      className="text-xs text-gray-600 hover:text-gray-900"
                      onClick={() => setCurrentView("list")}
                    >
                      Cancel
                    </button>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      className="px-4 py-2 text-xs bg-black text-white hover:bg-black/80 disabled:opacity-50"
                      onClick={handleCreateTemplate}
                      disabled={!newTemplate.description.trim() || !newTemplate.text.trim()}
                    >
                      Create Template
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              // Edit Template View
              <div className="max-w-3xl mx-auto">
                {/* Back Button */}
                <div className="mb-6">
                  <button
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-xs"
                    onClick={() => setCurrentView("list")}
                  >
                    <span>←</span> Back to Templates
                  </button>
                </div>

                {/* Template Name */}
                <div className="pb-4">
                  <div className="flex items-center">
                    <div className="w-24 text-xs text-gray-900">Name</div>
                    <input
                      className="flex-1 bg-transparent text-xs text-gray-900 placeholder-gray-400 focus:outline-none border-none"
                      placeholder="Enter template name..."
                      value={editDraft.name || ""}
                      onChange={(e) => setEditDraft((p) => ({ ...p, name: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Separator Line */}
                <div className="border-t border-gray-200"></div>

                {/* Template Description */}
                <div className="py-4">
                  <div className="flex items-center">
                    <div className="w-24 text-xs text-gray-900">Description</div>
                    <input
                      className="flex-1 bg-transparent text-xs text-gray-900 placeholder-gray-400 focus:outline-none border-none"
                      placeholder="Brief description (optional)..."
                      value={editDraft.description || ""}
                      onChange={(e) => setEditDraft((p) => ({ ...p, description: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Separator Line */}
                <div className="border-t border-gray-200"></div>

                {/* Template Type */}
                <div className="py-4">
                  <div className="flex items-center">
                    <div className="w-24 text-xs text-gray-900">Type</div>
                    <div className="flex border border-gray-300 rounded-sm overflow-hidden">
                      <button
                        type="button"
                        className={`px-2 py-1 text-xs font-medium transition ${
                          editDraft.type === "email"
                            ? "bg-black text-white"
                            : "bg-white text-gray-700 hover:bg-gray-100"
                        }`}
                        onClick={() => setEditDraft((p) => ({ ...p, type: "email" }))}
                      >
                        Email
                      </button>
                      <button
                        type="button"
                        className={`px-2 py-1 text-xs font-medium transition ${
                          editDraft.type === "linkedin"
                            ? "bg-black text-white"
                            : "bg-white text-gray-700 hover:bg-gray-100"
                        }`}
                        onClick={() => setEditDraft((p) => ({ ...p, type: "linkedin" }))}
                      >
                        LinkedIn
                      </button>
                    </div>
                  </div>
                </div>

                {/* Separator Line */}
                <div className="border-t border-gray-200"></div>

                {/* Variables Section */}
                <div className="py-3">
                  <div className="flex items-center">
                    <div className="text-xs text-gray-800">Variables</div>
                    <div className="ml-6 flex gap-6">
                      <Token label="{firstName}" onInsert={(token) => {
                        const textarea = document.querySelector('textarea[placeholder="Write your email template..."]') as HTMLTextAreaElement;
                        if (textarea) {
                          const start = textarea.selectionStart || 0;
                          const end = textarea.selectionEnd || 0;
                          const value = textarea.value || "";
                          const newValue = value.slice(0, start) + token + value.slice(end);
                          setEditDraft((p) => ({ ...p, text: newValue }));
                          requestAnimationFrame(() => {
                            const pos = start + token.length;
                            textarea.setSelectionRange(pos, pos);
                            textarea.focus();
                          });
                        }
                      }} />
                      <Token label="{lastName}" onInsert={(token) => {
                        const textarea = document.querySelector('textarea[placeholder="Write your email template..."]') as HTMLTextAreaElement;
                        if (textarea) {
                          const start = textarea.selectionStart || 0;
                          const end = textarea.selectionEnd || 0;
                          const value = textarea.value || "";
                          const newValue = value.slice(0, start) + token + value.slice(end);
                          setEditDraft((p) => ({ ...p, text: newValue }));
                          requestAnimationFrame(() => {
                            const pos = start + token.length;
                            textarea.setSelectionRange(pos, pos);
                            textarea.focus();
                          });
                        }
                      }} />
                      <Token label="{company}" onInsert={(token) => {
                        const textarea = document.querySelector('textarea[placeholder="Write your email template..."]') as HTMLTextAreaElement;
                        if (textarea) {
                          const start = textarea.selectionStart || 0;
                          const end = textarea.selectionEnd || 0;
                          const value = textarea.value || "";
                          const newValue = value.slice(0, start) + token + value.slice(end);
                          setEditDraft((p) => ({ ...p, text: newValue }));
                          requestAnimationFrame(() => {
                            const pos = start + token.length;
                            textarea.setSelectionRange(pos, pos);
                            textarea.focus();
                          });
                        }
                      }} />
                      <Token label="{title}" onInsert={(token) => {
                        const textarea = document.querySelector('textarea[placeholder="Write your email template..."]') as HTMLTextAreaElement;
                        if (textarea) {
                          const start = textarea.selectionStart || 0;
                          const end = textarea.selectionEnd || 0;
                          const value = textarea.value || "";
                          const newValue = value.slice(0, start) + token + value.slice(end);
                          setEditDraft((p) => ({ ...p, text: newValue }));
                          requestAnimationFrame(() => {
                            const pos = start + token.length;
                            textarea.setSelectionRange(pos, pos);
                            textarea.focus();
                          });
                        }
                      }} />
                    </div>
                  </div>
                </div>

                {/* Template Body */}
                <div className="">
                  <textarea
                    className="w-full h-[250px] p-3 bg-transparent border border-gray-200 text-[13px] text-black placeholder-gray-400 focus:outline-none resize-none"
                    placeholder="Write your email template..."
                    value={editDraft.text || ""}
                    onChange={(e) => setEditDraft((p) => ({ ...p, text: e.target.value }))}
                  />
                </div>

                {/* Bottom Actions */}
                <div className="ml-5 flex items-center justify-between pt-4">
                  <div className="flex items-center">
                    <button
                      className="text-xs text-gray-600 hover:text-gray-900"
                      onClick={cancelEditTemplate}
                    >
                      Cancel
                    </button>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      className="px-4 py-2 text-xs bg-black text-white hover:bg-black/80 disabled:opacity-50"
                      onClick={handleUpdateTemplate}
                      disabled={!editDraft.name?.trim() || !editDraft.text?.trim()}
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}